import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, API_URL } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";

const EASE = [0.16, 1, 0.3, 1];

export default function CertificatePage() {
  const { index } = useParams();
  const { t } = useLanguage();
  const [pixel, setPixel] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/pixels/${index}/public`)
      .then((r) => setPixel(r.data))
      .catch(() => setNotFound(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const pageUrl = window.location.href;
  const shareText = pixel
    ? `${t("cert.shareText")} [${pixel.x}, ${pixel.y}] — ${t("hero.tagline")}`
    : t("hero.tagline");

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      toast.success(t("cert.copied"));
    } catch {
      toast.error(t("common.error"));
    }
  };

  return (
    <main className="min-h-screen bg-[#FAF8F5] pt-[57px]" data-testid="certificate-page">
      <div className="px-5 md:px-12 py-12 border-b border-border/60">
        <p className="text-[10px] tracking-[0.4em] text-golddark mb-4">{t("cert.eyebrow").toUpperCase()}</p>
        <h1 className="font-display font-black tracking-tighter text-4xl md:text-6xl" data-testid="certificate-title">
          {t("cert.title")}
        </h1>
      </div>

      <div className="px-5 md:px-12 py-14 grid lg:grid-cols-[1fr_320px] gap-10 items-start">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE }}
          className="border border-border/60 p-3"
        >
          {pixel ? (
            <img
              src={`${API_URL}/pixels/${index}/certificate.png`}
              alt={`${t("alts.cert")} [${pixel.x}, ${pixel.y}]`}
              className="w-full block"
              data-testid="certificate-image"
            />
          ) : (
            <div className="aspect-[1200/630] flex items-center justify-center text-xs tracking-[0.3em] text-muted-foreground">
              {(notFound ? t("cert.notFound") : t("common.loading")).toUpperCase()}
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          className="border border-border/60 p-8 space-y-6"
        >
          {pixel && (
            <div>
              <div className="text-[9px] tracking-[0.3em] text-muted-foreground">{t("grid.coords")}</div>
              <div className="font-mono text-3xl text-golddark tabular-nums" data-testid="certificate-coords">
                [{pixel.x}, {pixel.y}]
              </div>
              <div className="mt-2 text-sm text-[#3C3735]">{pixel.user_name}</div>
            </div>
          )}
          <p className="text-xs text-muted-foreground leading-relaxed">{t("cert.shareHint")}</p>
          <div className="space-y-3">
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(pageUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="share-x-btn"
              className="block text-center px-6 py-3 border border-border text-[10px] tracking-[0.25em] hover:bg-[#3C3735] hover:text-[#FAF8F5] transition-colors duration-200"
            >
              X / TWITTER
            </a>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="share-facebook-btn"
              className="block text-center px-6 py-3 border border-border text-[10px] tracking-[0.25em] hover:bg-[#3C3735] hover:text-[#FAF8F5] transition-colors duration-200"
            >
              FACEBOOK
            </a>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(shareText + " " + pageUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="share-whatsapp-btn"
              className="block text-center px-6 py-3 border border-border text-[10px] tracking-[0.25em] hover:bg-[#3C3735] hover:text-[#FAF8F5] transition-colors duration-200"
            >
              WHATSAPP
            </a>
            <button
              onClick={copyLink}
              data-testid="share-copy-btn"
              className="w-full px-6 py-3 border border-border text-[10px] tracking-[0.25em] hover:bg-[#3C3735] hover:text-[#FAF8F5] transition-colors duration-200"
            >
              {t("cert.copyLink").toUpperCase()}
            </button>
            {pixel && (
              <a
                href={`${API_URL}/pixels/${index}/certificate.png`}
                download={`pixel-${pixel.x}-${pixel.y}.png`}
                data-testid="certificate-download-btn"
                className="block text-center px-6 py-3 bg-[#7D8C77] text-[#FAF8F5] text-[10px] tracking-[0.25em] font-semibold hover:bg-[#3C3735] transition-colors duration-200"
              >
                {t("cert.download").toUpperCase()}
              </a>
            )}
          </div>
          <Link to="/grid" className="block text-center text-[10px] tracking-[0.25em] text-muted-foreground hover:text-golddark transition-colors duration-200" data-testid="certificate-back-link">
            ← {t("nav.grid").toUpperCase()}
          </Link>
        </motion.div>
      </div>
    </main>
  );
}
